/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/
import React, { useState, useCallback } from 'react';
import { useIntl, useLocales } from '@exo/frontend-common-i18n';
import { LayoutSpacing } from '@exo/frontend-components-core';
/* import { useHistory } from 'react-router-dom'; */
import { useSessionContext } from '@exo/frontend-common-session-context';
import {
  Address,
  AddressBookContainer,
  useCheckoutContext
} from '@exo/frontend-features-checkout-logic';
import { AddressInput } from '../../components/AddressInput/AddressInput';
import { CheckoutConfig } from '../../checkoutConfig';
import { AddressBookDropdown } from '../../components/AddressBookDropdown/AddressBookDropdown';
import * as S from '../PaymentPanel/PaymentPanel.styles';
import { DeliveryOptions } from '@exo/frontend-features-cart-ui';
import { DeliveryOptionsContainer, ShippingState } from '@exo/frontend-features-cart-logic';
import { useScrollViewportTo } from '@exo/frontend-common-hooks';

export const ShippingAddressPanel = ({ config }: Props) => {
  const { checkout, dispatch } = useCheckoutContext();
  const [addressFromBook, setAddressFromBook] = useState<Address>();
  const localesQ = useLocales();
  const session = useSessionContext();
  useScrollViewportTo(0, 0);
  const [cDate, setCDate] = useState<any>('');
  const [tempStore, setTempStore] = useState<any>('');

  const [shippingState, setShippingState] = useState<ShippingState>({
    storeId: session.storeId,
    storeName: session.storeName,
    collectionDate: cDate
  });

  const onPrevious = () => dispatch({ type: 'PREVIOUS' });
  const onNext = (addr: Address) => {
    if (tempStore === 'ClickNCollect') {
      session.set({pickNCollect: 'ClickNCollect'});
      checkout.context.pickup = 'ClickNCollect';
      checkout.context.useBillingAddress = true;
      addr = {
        address1: '8 The Links ',
        address2: 'St Andrews, Fife, KY16 9JB',
        city: 'St Andrews',
        countryCode: 'GB',
        country: 'GB',
        countryName: 'United Kingdom',
        firstName: 'The Old Course Shop ',
        lastName: 'The Old Course Shop ',
        name: 'The Old Course Shop ',
        phone: '+44 1334 466736',
        region: 'Fife',
        postalCode: 'KY16 9JB'
      };
    } else {
      checkout.context.pickup = '';
      session.set({pickNCollect: ''});
    }

    dispatch({
      type: 'NEXT',
      address: {
        ...addr,
        countryCode: addr.country,
        countryName: localesQ.data.find(d => d.isoCode === addr.country)?.name
      },
      collectionDate: tempStore === 'ClickNCollect' ? cDate : ''
    });
  };

  const onShippingModeUpdate = useCallback(
    (s: ShippingState) => {
      setTempStore(s.storeName);
      if (s.storeId && s.storeId !== session.storeId) {
        session.set({ storeId: s.storeId, storeName: s.storeName });
      }
      setShippingState(s);
      setCDate(s.collectionDate);
      checkout.context.shippingMode = s.shippingModeId;
    },
    [session]
  );

  const onToggleUseBillingAddress = () => dispatch({ type: 'TOGGLE_USE_BILLING_ADDRESS' });
  const intl = useIntl('features.checkout.checkout-ui.panels.ShippingAddressPanel');
  if (localesQ.loading) return <div>Loading...</div>;

  return (
    <div>
      <h2>{intl.msg('shippingadresspanel.shippingaddress', 'Shipping/Delivery Address')}</h2>
      <LayoutSpacing size="sm" />
      <S.PaymentHeader>{'Delivery Type'}</S.PaymentHeader>
      <LayoutSpacing size="sm" />
      <DeliveryOptionsContainer
        cartId={session.cartId!}
        shippingState={shippingState}
        onShippingModeUpdate={onShippingModeUpdate}
        render={zipInArgs => <DeliveryOptions {...zipInArgs} />}
        renderLoading={() => <DeliveryOptions.Skeleton />}
      />
      <LayoutSpacing size="xs" />
      {config.feature.addressBook !== 'none' && tempStore !== 'ClickNCollect' &&  (
        <AddressBookContainer
          type={config.feature.addressBook}
          render={({ addresses }) => (
            <AddressBookDropdown
              addresses={addresses}
              onAddressSelect={e => setAddressFromBook(e)}
              selectedAddress={addressFromBook}
            />
          )}
        />
      )}

      <AddressInput
        storeName={tempStore}
        data-testid="addressInput"
        countriesList={localesQ.dataForForm}
        addressSearch={config.feature.addressSearch}
        type="shipping"
        addressToDisplay={addressFromBook ?? checkout.context?.shippingAddress}
        isBillingEnabled={checkout.context.useBillingAddress ?? false}
        onToggleUseBillingAddress={onToggleUseBillingAddress}
        onFormSubmit={onNext}
        onFormBack={onPrevious}
      />
    </div>
  );
};

type Props = {
  config: CheckoutConfig;
};
